/**
 * Progreso de la conversación D-Talk — una sola definición para backend y front
 * (mismo patrón que shared/dtalk-stats.ts).
 *
 * El progreso anterior dependía de `cantidadPreguntasPrincipales`, un número
 * que auto-reporta el LLM en su bloque <D_TALK_ACTION> (default 0): si el
 * modelo reportaba 0 durante varios turnos y de golpe 5-6, la barra saltaba de
 * 0% a completo. Este cálculo es determinístico y MONÓTONO: cada turno del
 * usuario garantiza avance perceptible, y el resultado nunca retrocede porque
 * se toma el máximo contra el valor persistido del turno anterior.
 */

export interface DTalkProgressInput {
  /** Turnos del usuario en la conversación (mensajes con role 'user'). */
  userTurns: number;
  /** Preguntas principales según el agente (puede venir 0 o desactualizado). */
  cantidadPreguntasPrincipales?: number | null;
  /** Los 4 campos de "comprensión" (los mismos dots del panel del chat). */
  estadoActual?: string | null;
  aspiracionPrincipal?: string | null;
  competenciasADesarrollar?: string[] | null;
  responsabilidadesInteres?: string[] | null;
  processStatus?: string | null;
  /** Valor persistido del turno anterior — garantiza monotonicidad. */
  prevProgressPct?: number | null;
}

const TOTAL_QUESTIONS = 6;

export function computeDTalkProgress(input: DTalkProgressInput): number {
  if (input.processStatus === 'completed') return 100;

  // Arranque: elegir idioma / primer mensaje ya es avance visible
  const start = input.userTurns >= 1 ? 8 : 0;

  // Preguntas: el conteo del agente o, si viene bajo, los turnos reales del
  // usuario (cap 6) — así la barra avanza en cada mensaje aunque el modelo
  // reporte 0.
  const questions = Math.min(
    Math.max(input.cantidadPreguntasPrincipales ?? 0, Math.min(input.userTurns, TOTAL_QUESTIONS)),
    TOTAL_QUESTIONS,
  );
  const questionsPts = (questions / TOTAL_QUESTIONS) * 44;

  // Comprensión: 7 puntos por cada dimensión estructurada capturada
  const understanding =
    (input.estadoActual ? 7 : 0) +
    (input.aspiracionPrincipal ? 7 : 0) +
    ((input.competenciasADesarrollar?.length ?? 0) > 0 ? 7 : 0) +
    ((input.responsabilidadesInteres?.length ?? 0) > 0 ? 7 : 0);

  // Conclusión presentada (pendiente de validación)
  const conclusion = input.processStatus === 'pending_validation' ? 12 : 0;

  // Cap 92 hasta validar: el 100 queda reservado para 'completed'. Una
  // conversación reabierta conserva su máximo previo pero vuelve al cap (la
  // única transición que "baja" es 100 → 92 al reabrir, deliberada).
  const computed = Math.round(start + questionsPts + understanding + conclusion);
  return Math.min(Math.max(input.prevProgressPct ?? 0, computed), 92);
}
